/**
 * 이 탭이 **검증한** 인증 저장 원문 — 공유 localStorage 의 **암호문**과, 그 암호문을 실제로 복호화한 **평문**을 짝으로 기억한다.
 *
 * 왜(5회차 검증 P2): react-secure-storage 는 **저장 당시** 브라우저 지문(색심도 등)으로 암호화한다. 저장 뒤 지문이 바뀌면
 * (모니터 이동 등) 지금 지문으로는 그 암호문을 풀 수 없어, 공유 저장소 읽기(lib/sharedAuthStorage)가 정상 세션을 null 로
 * 보고 관문이 결제 성공 콜백까지 건너뛰었다.
 *
 * 규칙: 복호화에 실패해도 **localStorage 의 암호문이 여기 기억한 암호문과 글자 하나까지 같으면** 다른 탭이 바꾸지 않은 것이므로
 * 기억한 평문을 지금 값으로 쓴다. 다르거나 기억이 없으면 null(차단) 그대로다. 「실패하면 탭 메모리」로 대신하지 않는다.
 *
 * 짝은 **검증된 것만** 기록한다(6회차 검증 P1): localStorage 에서 암호문을 한 번 읽고, **그 같은 문자열**을 복호화해 저장 형식
 * 검사를 통과했을 때만 (그 암호문, 그 복호화 결과)를 기록한다. 라이브러리 메모리 평문과 따로 읽은 암호문을 짝짓지 않는다 —
 * 그 사이 다른 탭이 쓰면 B 암호문 ↔ A 평문이 남는다.
 *  (a) 탭 시작: 그때의 암호문을 복호화해 검증되면 기록(실패하면 기록 안 함).
 *  (b) 이 탭이 setItemWithExpiration 으로 쓴 직후: 원문을 읽어 복호화한 결과가 **방금 쓴 평문과 같을 때만** 기록
 *      (다르면 그 사이 다른 탭이 끼어든 것 — 기록 안 함).
 *  (c) 공유 읽기에서 복호화·검증에 성공했을 때.
 * 복호화 결과가 저장 형식이 아니면(잘못된 키로 나온 쓰레기 문자열 등) 실패로 보고, 기존 정상 짝을 덮지 않는다.
 */
export const AUTH_CIPHER_KEYS = ['authorization', 'authObject', 'refreshToken'] as const;

type Memo = { cipher: string; plaintext: string };
const memo = new Map<string, Memo>();

/** setItemWithExpiration 형식 `{ value, expirationTime }` 의 JSON 문자열을 읽는다. 형식이 아니면 null. */
export const parseStoredWithExpiration = (raw: unknown): { value: any; expirationTime: number } | null => {
    if (typeof raw !== 'string' || !raw) return null;
    try {
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
        if (typeof parsed.expirationTime !== 'number' || !Object.prototype.hasOwnProperty.call(parsed, 'value')) return null;
        return parsed;
    } catch {
        return null;
    }
};

/** 라이브러리 키 형식 그대로: `<접두사><타입>.<키>`. setItemWithExpiration 은 JSON 문자열을 저장하므로 타입은 `s`. */
export const authStorageKeyOf = (name: string): string | null => {
    try {
        // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
        const utils = require('react-secure-storage/dist/utils');
        return `${utils.getSecurePrefix()}s.${name}`;
    } catch {
        return null;
    }
};

export const readAuthCipher = (name: string): string | null => {
    try {
        const key = authStorageKeyOf(name);
        return key ? localStorage.getItem(key) : null;
    } catch {
        return null;
    }
};

type Decryptor = { decrypt: (value: string) => string | null };
type DecryptorClass = new () => Decryptor;
let decryptorClass: DecryptorClass | null | undefined;
/**
 * 복호화할 때마다 **새 복호화기**를 만든다 — 라이브러리도 저장할 때마다 새 EncryptionService 를 만들어 그때의 브라우저 지문으로
 * 키를 정한다(react-secure-storage/dist/index.js setItem). 첫 인스턴스를 계속 쓰면 지문이 바뀐 뒤의 정상 저장을 못 읽는다.
 */
const createDecryptor = (): Decryptor | null => {
    if (decryptorClass === undefined) {
        try {
            // 라이브러리가 쓰는 것과 같은 복호화. 공개 API 가 원문 다시 읽기를 주지 않아 내부 모듈을 쓴다.
            // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
            const mod = require('react-secure-storage/dist/encryption');
            decryptorClass = (mod?.default ?? mod) as DecryptorClass;
        } catch {
            decryptorClass = null;
        }
    }
    if (!decryptorClass) return null;
    try {
        return new decryptorClass();
    } catch {
        return null;
    }
};

/** 암호문 하나를 지금 지문으로 복호화해 저장 형식까지 맞으면 평문, 아니면 null. */
export const decryptVerified = (cipher: string): string | null => {
    try {
        const d = createDecryptor();
        const plaintext = d ? d.decrypt(cipher) : null;
        return plaintext && parseStoredWithExpiration(plaintext) ? plaintext : null;
    } catch {
        return null;
    }
};

/** (c) 검증된 짝(같은 암호문 문자열을 복호화·검증한 결과)만 기록한다. */
export const rememberVerifiedAuthCipher = (name: string, cipher: string, verifiedPlaintext: string) => {
    memo.set(name, { cipher, plaintext: verifiedPlaintext });
};

/** 암호문 하나를 읽어 그 문자열을 복호화·검증한다. expected 가 있으면 그것과 같을 때만. 성공하면 기록. */
const verifyAndRemember = (name: string, expected?: string) => {
    const cipher = readAuthCipher(name);
    if (!cipher) return;
    const plaintext = decryptVerified(cipher);
    if (!plaintext) return;
    if (expected !== undefined && plaintext !== expected) return;
    rememberVerifiedAuthCipher(name, cipher, plaintext);
};

/** (b) 이 탭이 방금 쓴 값 — 원문을 읽어 복호화한 결과가 방금 쓴 평문과 같을 때만 기록(아니면 다른 탭이 끼어든 것). */
export const rememberOwnAuthWrite = (name: string, writtenPlaintext: string) => verifyAndRemember(name, writtenPlaintext);

/** 이 암호문을 이 탭이 검증해 안다면 그 평문, 아니면 null. */
export const knownPlaintextFor = (name: string, cipher: string): string | null => {
    const known = memo.get(name);
    return known && known.cipher === cipher ? known.plaintext : null;
};

/** (a) 탭 시작 — 그때의 암호문을 복호화해 검증되면 기록. */
AUTH_CIPHER_KEYS.forEach((name) => {
    try {
        verifyAndRemember(name);
    } catch {
        // 기록 못 하면 복호화 실패 때 차단될 뿐이다.
    }
});
